import React, { useEffect, useState } from "react";
import axios from "axios";
import { Globe, Check, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DomainsPage() {
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/plans/category/domains`).then((r) => setPlans(r.data)).catch(console.error);
  }, []);

  const popularTlds = [".com", ".in", ".ai", ".net", ".org", ".co.in", ".io"];
  const colorFor = (tld) => {
    const map = { ".com": "#00F3FF", ".in": "#00FF94", ".ai": "#BC13FE", ".net": "#ffcc00", ".org": "#00F3FF", ".co.in": "#00FF94", ".io": "#BC13FE" };
    return map[tld] || "#00F3FF";
  };

  return (
    <div data-testid="domains-page" className="page-enter pt-24">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,243,255,0.1) 0%, transparent 60%), #030305" }}>
        <div className="animated-grid absolute inset-0 opacity-30" />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.25)", color: "#00F3FF" }}>
            <Globe size={12} /> DOMAIN REGISTRATION
          </div>
          <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-4">
            Find Your Perfect <span className="text-gradient">Domain</span>
          </h1>
          <p className="font-outfit text-base text-gray-400 mb-8" style={{ fontWeight: 300 }}>
            .in domains from just ₹299/yr. .com from ₹899/yr. WHOIS privacy included.
          </p>

          {/* Search box */}
          <div className="relative max-w-xl mx-auto">
            <div className="flex items-center gap-3 p-4 rounded-2xl"
              style={{ background: "rgba(10,10,18,0.95)", border: "1px solid rgba(0,243,255,0.25)" }}>
              <Search size={18} color="#00F3FF" className="flex-shrink-0" />
              <input
                data-testid="domain-search"
                type="text"
                placeholder="Search for your domain name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent outline-none font-outfit text-sm text-gray-200 placeholder-gray-600"
              />
              <button className="btn-primary text-xs px-5 py-2 font-700" style={{ fontSize: "12px" }}>SEARCH</button>
            </div>
          </div>

          {/* TLD quick links */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
            {popularTlds.map((tld) => (
              <span key={tld} className="px-3 py-1 rounded-full text-xs font-rajdhani font-700 cursor-pointer hover:scale-105 transition-transform"
                style={{ background: `${colorFor(tld)}12`, border: `1px solid ${colorFor(tld)}30`, color: colorFor(tld) }}>
                {tld}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Domain Plans */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-orbitron font-700 text-2xl text-white text-center mb-10">
            Domain <span className="text-gradient">Pricing</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {plans.map((plan) => {
              const tld = plan.specs?.extension || ".com";
              const color = colorFor(tld);
              return (
                <div key={plan.id} data-testid={`domain-card-${plan.id}`}
                  className="glass rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300 relative"
                  style={{ border: `1px solid ${color}18` }}>
                  {plan.badge && (
                    <span className="absolute top-3 right-3 text-xs font-rajdhani font-700 px-2 py-0.5 rounded-full"
                      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
                      {plan.badge}
                    </span>
                  )}
                  <div className="font-orbitron font-900 text-3xl mb-1" style={{ color }}>{tld}</div>
                  <div className="font-outfit text-xs text-gray-500 mb-4" style={{ fontWeight: 300 }}>{plan.name}</div>
                  <div className="flex items-end gap-1 mb-4">
                    <span className="font-orbitron font-900 text-2xl text-white">₹{plan.price.toLocaleString("en-IN")}</span>
                    <span className="text-gray-500 text-xs font-outfit mb-0.5">/yr</span>
                    {plan.original_price && (
                      <span className="text-gray-600 text-xs line-through ml-2 font-outfit mb-0.5">₹{plan.original_price.toLocaleString("en-IN")}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 mb-5">
                    {plan.features.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <Check size={11} style={{ color, flexShrink: 0 }} />
                        <span className="font-outfit text-xs text-gray-400" style={{ fontWeight: 300 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <button className="w-full py-2.5 rounded-xl font-rajdhani font-700 text-xs tracking-wider transition-all"
                    style={{ background: `${color}10`, border: `1px solid ${color}25`, color }}>
                    REGISTER {tld}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12" style={{ background: "rgba(10,10,18,0.4)" }}>
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {[
            { title: "Free WHOIS Privacy", desc: "Your personal info stays hidden" },
            { title: "Easy DNS Management", desc: "Full control over your DNS records" },
            { title: "Auto Renewal Option", desc: "Never lose your domain accidentally" },
          ].map((b) => (
            <div key={b.title} className="text-center">
              <h3 className="font-rajdhani font-700 text-base text-white mb-2">{b.title}</h3>
              <p className="font-outfit text-sm text-gray-500" style={{ fontWeight: 300 }}>{b.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
